/* Request-quote mode for design PDPs (client half).
   Server renders #bd-buybox[data-quote="1"] when the product isn't buyable (bdw.buyable
   metafield, else theme setting quote_mode_default). Here:
   1. session unlock — only ?buy=1 persists sessionStorage.bd_unlock=1; unlocked sessions get
      the normal buy box. ?buy=0 clears the unlock (testing / support links back to quote mode).
      UTM and fitment params are for attribution/preselection only.
   2. "Request a quote" opens the side panel (#bdqp, snippets/bd-quote-mode.liquid) with the
      build read from the buy box AT CLICK TIME — finish swatch, front/rear size + qty, saved
      BDFitCore vehicle — so it can't go stale (2026-09-29 fix: the old href-only prefill was
      refreshed by bubbling click/change events, which ran before the vehicle's async resolve
      and never after programmatic selection, and its bd:vehicle-changed listener sat on
      document while the core dispatches on window). Editing happens in place in the panel.
   3. The CTA href stays prefilled (/pages/request-a-quote?wheel=&finish=&size=&vehicle=) as
      the no-JS / open-in-new-tab fallback into the full-page form.
   PostHog: quote_cta_clicked (click), quote_form_express (panel opened, build prefilled),
   lead_submitted (fired by the snippet on the ?contact_posted=true reload). */
(() => {
  const general = document.getElementById("bdqp")?.dataset.general === "true";
  const box = document.getElementById("bd-buybox") || document.querySelector(".bd-series") || document.body;
  const wrap = document.getElementById("bdbb-quote-wrap");
  const cta = document.getElementById("bdbb-quote") || document.querySelector("[data-series-quote], [data-header-quote]");
  const panel = document.getElementById("bdqp");
  if (!box || !cta) return;

  const q = new URLSearchParams(location.search);
  const UNLOCK = q.get("buy") === "1";
  const RELOCK = q.get("buy") === "0"; // testing: ?buy=0 ends the session unlock
  try { if (RELOCK) sessionStorage.removeItem("bd_unlock"); else if (UNLOCK) sessionStorage.setItem("bd_unlock", "1"); } catch (e) {}
  let unlocked = false;
  try { unlocked = sessionStorage.getItem("bd_unlock") === "1"; } catch (e) {}
  if (!general && unlocked && box.dataset.quote === "1") { box.dataset.quote = "0"; box.dataset.unlocked = "1"; }

  // ---- panel plumbing ----
  const $ = (id) => document.getElementById(id);
  const val = (id) => { const el = $(id); return el ? el.value : ""; };
  const openPanel = () => {
    if (!panel) return false;
    try { if (!panel.open) panel.showModal(); } catch (e) { panel.setAttribute("open", ""); }
    return true;
  };
  const closePanel = () => { if (!panel) return; try { panel.close(); } catch (e) { panel.removeAttribute("open"); } };
  if (panel) {
    panel.addEventListener("click", (e) => {
      if (e.target.closest("[data-bdqp-close]")) { closePanel(); return; }
      if (e.target === panel) closePanel(); // backdrop
      if (e.target.closest("[data-bdqp-edit]")) panel.classList.add("is-editing");
    });
  }
  // ---- read the build from the buy box (exact PDP values, nothing inferred) ----
  const loadSaved = () => {
    try {
      const C = window.BDFitCore;
      return C && C.loadVehicle ? C.loadVehicle() : JSON.parse(localStorage.getItem("bdf_vehicle_v4") || "null");
    } catch (e) { return null; }
  };
  function readBuild() {
    const sw = box.querySelector('#bdbb-finish [aria-checked="true"]');
    const fSel = $("bdbb-front-size"), rSel = $("bdbb-rear-size");
    const opts = (sel) => sel ? [].slice.call(sel.options).map((o) => o.value).filter(Boolean) : [];
    const saved = loadSaved();
    const veh = saved && (saved.vehicle || saved);
    const label = (veh && veh.label) || "";
    const trim = (saved && saved.trim) || "";
    // label = "<year> <Make> <Model>[ <trim>]"; the make slug tells how many words the make is
    let year = "", make = "", model = "";
    const m = label.match(/^(\d{4})\s+(.+)$/);
    if (m) {
      year = m[1];
      let rest = m[2];
      if (trim && rest.endsWith(" " + trim)) rest = rest.slice(0, -(trim.length + 1));
      const words = rest.split(/\s+/);
      const n = Math.max(1, Math.min(words.length - 1, ((saved && saved.slugs && saved.slugs.make) || "").split("-").filter(Boolean).length || 1));
      make = words.slice(0, n).join(" "); model = words.slice(n).join(" ");
    }
    return {
      design: (wrap && wrap.dataset.design) || (panel && panel.dataset.design) || "",
      finish: (sw && sw.dataset.finish) || "",
      finishes: [].slice.call(box.querySelectorAll("#bdbb-finish [data-finish]")).map((b) => b.dataset.finish),
      front: fSel ? fSel.value : "", rear: rSel ? rSel.value : "",
      sizes: Array.from(new Set(opts(fSel).concat(opts(rSel)))),
      fqty: +val("bdbb-front-qty") || 0, rqty: +val("bdbb-rear-qty") || 0,
      label, trim, year, make, model,
    };
  }
  const sizeText = (b) => {
    if (!b.front && !b.rear) return "";
    const total = b.fqty + b.rqty || 4;
    if (!b.rear || b.front === b.rear) return `${b.front}" ×${total}`;
    return `F ${b.front}" ×${b.fqty} / R ${b.rear}" ×${b.rqty}`;
  };

  // ---- fill the panel ----
  const fillSelect = (sel, values, current) => {
    if (!sel) return;
    sel.innerHTML = "";
    const list = values.slice();
    if (current && list.indexOf(current) < 0) list.unshift(current);
    if (!list.length) list.push("");
    list.forEach((v) => { const o = document.createElement("option"); o.value = v; o.textContent = v || "—"; sel.appendChild(o); });
    sel.value = current || list[0];
  };
  function fillPanel(b) {
    if (!panel) return;
    const view = (row, text) => { const r = panel.querySelector(`[data-bdqp-row="${row}"] [data-bdqp-view]`); if (r) r.textContent = text; };
    const field = (k) => panel.querySelector(`[data-bdqp-field="${k}"]`);
    const hidden = (k, v) => { const h = panel.querySelector(`[data-bdqp-hidden="${k}"]`); if (h) h.value = v; };
    view("finish", b.finish); fillSelect(field("finish"), b.finishes, b.finish);
    view("size", sizeText(b)); fillSelect(field("front"), b.sizes, b.front); fillSelect(field("rear"), b.sizes, b.rear || b.front);
    view("vehicle", b.label);
    if (field("year")) field("year").value = b.year;
    if (field("make")) field("make").value = b.make;
    if (field("model")) field("model").value = b.model;
    hidden("fqty", String(b.fqty)); hidden("rqty", String(b.rqty)); hidden("trim", b.trim); hidden("vehicle", b.label);
    const vrow = panel.querySelector('[data-bdqp-row="vehicle"]'), hint = panel.querySelector("[data-bdqp-vehicle-hint]");
    if (vrow) vrow.classList.toggle("is-open", !b.label);
    if (hint) hint.hidden = !!b.label;
    panel.classList.remove("is-editing");
  }

  // Shopify posted back (?contact_posted=true → thank-you, or validation errors): show it
  // regardless of quote/unlock state, then drop the param so a reload is clean.
  const posted = panel && panel.querySelector("[data-bdqp-state]");
  if (posted) {
    if (posted.dataset.bdqpState === "error") { fillPanel(readBuild()); panel.classList.add("is-editing"); }
    openPanel();
    if (q.has("contact_posted")) {
      q.delete("contact_posted");
      try { history.replaceState(null, "", location.pathname + (q.toString() ? "?" + q : "") + location.hash); } catch (e) {}
    }
  }

  // ---- fallback href for the full-page form (no JS / new tab) ----
  const vehicleLabel = () => { const s = loadSaved(); const v = s && (s.vehicle || s); return (v && v.label) || ""; };
  function refresh() {
    const b = readBuild();
    const p = new URLSearchParams(location.search);
    p.set("wheel", b.design);
    if (b.finish) p.set("finish", b.finish);
    if (b.front) p.set("size", sizeText(b));
    const veh = vehicleLabel(); if (veh) p.set("vehicle", veh);
    document.querySelectorAll("#bdbb-quote, [data-series-quote], [data-header-quote]").forEach(link => { link.href = "/pages/request-a-quote?" + p.toString(); });
  }
  refresh();
  box.addEventListener("click", () => setTimeout(refresh, 0));
  box.addEventListener("change", refresh);
  box.addEventListener("input", refresh);
  window.addEventListener("bd:vehicle-changed", refresh);   // BDFitCore dispatches on window
  cta.addEventListener("pointerdown", refresh);              // last chance before a new-tab open

  const triggers = document.querySelectorAll("#bdbb-quote, [data-series-quote], [data-header-quote]");
  triggers.forEach(trigger => trigger.addEventListener("click", (e) => {
    refresh();
    const b = readBuild();
    window.posthog && posthog.capture("quote_cta_clicked", { design: b.design, href: cta.href, panel: !!panel });
    if (!panel || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // let new-tab opens hit the page form
    e.preventDefault();
    fillPanel(b);
    if (openPanel()) {
      const first = panel.querySelector('input[name="contact[First name]"]');
      if (first) setTimeout(() => first.focus(), 50);
      window.posthog && posthog.capture("quote_form_express", { wheel: b.design, finish: b.finish, front: b.front, rear: b.rear,
        vehicle: !!b.label, source: general ? "site-panel" : "pdp-panel" });
    }
  }));
})();
